import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutTemplate, Sparkles, X } from 'lucide-react';
import { useFluidFormConfigClient } from './fluidFormConfig.client';
import { DescriptionWithTags } from '../components/DescriptionWithTags';
export const FormTemplatesSlider = ({ onNewForm, isOpen: controlledIsOpen, onOpenChange, hideButton = false, }) => {
    const client = useFluidFormConfigClient();
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    // Support both controlled and uncontrolled modes
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
    const setIsOpen = (open) => {
        if (onOpenChange) {
            onOpenChange(open);
        }
        else {
            setInternalIsOpen(open);
        }
    };
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await client.listTemplates();
                if (!mounted)
                    return;
                setTemplates(data);
            }
            catch (e) {
                if (!mounted)
                    return;
                setError('Failed to load templates.');
            }
            finally {
                if (!mounted)
                    return;
                setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);
    const items = useMemo(() => {
        return (templates || []).filter(t => !!t && t.isTemplate);
    }, [templates]);
    const openTemplate = (t) => {
        const orgId = t.orgId;
        if (!orgId) {
            setError('Template is missing orgId.');
            return;
        }
        setIsOpen(false);
        navigate(`/form-config/templates/${encodeURIComponent(orgId)}/${encodeURIComponent(t.formKey)}`);
    };
    return (React.createElement("div", { className: hideButton ? '' : 'mb-8' },
        !hideButton && (React.createElement("div", { className: "flex items-center justify-between" },
            React.createElement("div", null),
            React.createElement("button", { onClick: () => setIsOpen(true), className: "font-medium transition-all duration-300 flex items-center justify-center px-4 py-2 text-sm rounded-md bg-transparent border border-gray-300 hover:bg-gray-50 text-gray-700" },
                React.createElement(LayoutTemplate, { className: "w-5 h-5 mr-2" }),
                "Browse Templates"))),
        isOpen && (React.createElement("div", { className: "fixed inset-0 bg-black/40 backdrop-blur-sm z-50", onClick: () => setIsOpen(false) })),
        React.createElement("div", { className: `fixed inset-y-0 right-0 w-full md:w-[500px] bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`, role: "dialog", "aria-modal": "true", "aria-label": "Form Templates" },
            React.createElement("div", { className: "flex items-center justify-between p-6 border-b border-gray-100" },
                React.createElement("div", { className: "flex items-center" },
                    React.createElement("div", { className: "w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3" },
                        React.createElement(LayoutTemplate, { className: "w-5 h-5 text-blue-600" })),
                    React.createElement("div", null,
                        React.createElement("h2", { className: "text-2xl font-bold text-gray-900" }, "Form Templates"),
                        React.createElement("p", { className: "text-sm text-gray-500" }, "Choose a template to get started"))),
                React.createElement("button", { onClick: () => setIsOpen(false), className: "p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500", "aria-label": "Close" },
                    React.createElement(X, { className: "w-5 h-5" }))),
            React.createElement("div", { className: "flex-1 overflow-y-auto p-6" },
                error && (React.createElement("div", { className: "mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" }, error)),
                loading ? (React.createElement("div", { className: "space-y-4" }, [0, 1, 2, 3, 4].map((i) => (React.createElement("div", { key: i, className: "bg-white p-5 rounded-xl border-2 border-gray-200" },
                    React.createElement("div", { className: "h-4 w-40 bg-gray-100 rounded mb-3" }),
                    React.createElement("div", { className: "h-3 w-64 bg-gray-100 rounded mb-2" }),
                    React.createElement("div", { className: "h-3 w-52 bg-gray-100 rounded" })))))) : items.length === 0 ? (React.createElement("div", { className: "rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600" }, "No templates available yet.")) : (React.createElement("div", { className: "space-y-4" }, items.map((t) => ((() => {
                    const desc = t?.formConfig?.description || '';
                    return (React.createElement("div", { key: `${t.orgId || 'unknown'}:${t.formKey}`, className: "group bg-white p-5 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:shadow-md cursor-pointer transition-all", onClick: () => openTemplate(t), role: "button", tabIndex: 0, onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openTemplate(t);
                            }
                        } },
                        React.createElement("div", { className: "flex items-start justify-between mb-3" },
                            React.createElement("div", { className: "flex-1" },
                                React.createElement("div", { className: "flex items-center mb-2" },
                                    React.createElement("h3", { className: "text-base font-bold text-gray-900 group-hover:text-blue-600 transition-colors" }, t.name || t.formKey)),
                                React.createElement(DescriptionWithTags, { description: desc })),
                            React.createElement("div", { className: "w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center ml-4 group-hover:bg-blue-50 transition-colors" },
                                React.createElement(LayoutTemplate, { className: "w-6 h-6 text-gray-400 group-hover:text-blue-600 transition-colors" })))));
                })())))),
                React.createElement("div", { className: "mt-8 p-6 bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-xl border border-orange-200" },
                    React.createElement("div", { className: "flex items-start" },
                        React.createElement("div", { className: "w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" },
                            React.createElement(Sparkles, { className: "w-5 h-5 text-white" })),
                        React.createElement("div", null,
                            React.createElement("h4", { className: "font-semibold text-gray-900 mb-2" }, "Can't find what you need?"),
                            React.createElement("p", { className: "text-sm text-gray-600 mb-4" }, "Describe your form in plain English and let our AI build it for you."),
                            React.createElement("button", { className: "font-medium transition-all duration-300 flex items-center justify-center px-3 py-1.5 text-xs rounded-md bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md", onClick: () => {
                                    setIsOpen(false);
                                    onNewForm?.();
                                } },
                                React.createElement(Sparkles, { className: "w-4 h-4 mr-2" }),
                                "Build with AI"))))))));
};
export default FormTemplatesSlider;
